/**
 * 操作系统类型
 */
export type OS = 'ios' | 'android' | 'harmonyos' | 'windows' | 'mac' | 'linux' | 'unknown';

/**
 * 操作系统信息
 */
export interface OSInfo {
  /**
   * 操作系统类型
   */
  sys: OS;
  /**
   * 操作系统版本号
   */
  version: string;
}

/**
 * 获取当前操作系统信息
 *
 * @returns {OSInfo} 操作系统信息，包含系统类型和版本号
 *
 * @example
 * ```ts
 * const { sys, version } = getOS();
 * if (sys === 'ios') {
 *   // iOS 系统处理
 * }
 * ```
 */
export function getOS(): OSInfo {
  try {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
      return { sys: 'unknown', version: '' };
    }

    const ua = navigator.userAgent.toLowerCase();

    // HarmonyOS: HarmonyOS X.Y or HarmonyOS X.Y.Z
    if (ua.includes('harmonyos') || ua.includes('openharmony')) {
      const match = ua.match(/harmonyos[\s/]?(\d+\.\d+(?:\.\d+)?)/);
      return { sys: 'harmonyos', version: match ? match[1] : '' };
    }

    // iOS: iPhone, iPad, iPod - iOS_X_Y or OS X_Y
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) {
      // iOS 13+ format: "iPhone OS X_Y" or older "OS X_Y"
      const match = ua.match(/(?:iphone\s+os|os)\s+(\d+[._]\d+)/);
      if (match) {
        return { sys: 'ios', version: match[1].replace('_', '.') };
      }
      // Older format
      const oldMatch = ua.match(/os\s+(\d+)\s+(\d+)/);
      if (oldMatch) {
        return { sys: 'ios', version: `${oldMatch[1]}.${oldMatch[2]}` };
      }
      return { sys: 'ios', version: '' };
    }

    // Android: Android X.Y or Android X
    if (ua.includes('android')) {
      const match = ua.match(/android\s+(\d+(?:\.\d+)?)/);
      return { sys: 'android', version: match ? match[1] : '' };
    }

    // Windows: Windows NT X.Y -> Windows X.Y
    if (ua.includes('windows nt')) {
      const ntMatch = ua.match(/windows\s+nt\s+(\d+\.\d+)/);
      if (ntMatch) {
        // Windows NT version mapping
        const ntVersion = parseFloat(ntMatch[1]);
        const windowsVersion = ntVersion >= 10.0 ? '10/11' : `NT ${ntVersion}`;
        return { sys: 'windows', version: windowsVersion };
      }
      return { sys: 'windows', version: '' };
    }

    // Mac: Macintosh or Mac OS X
    if (ua.includes('macintosh') || ua.includes('mac os x')) {
      const match = ua.match(/mac\s+os\s+x\s+(\d+[._]\d+(?:[._]\d+)?)/);
      if (match) {
        return { sys: 'mac', version: match[1].replace(/_/g, '.') };
      }
      return { sys: 'mac', version: '' };
    }

    // Linux
    if (ua.includes('linux')) {
      return { sys: 'linux', version: '' };
    }

    return { sys: 'unknown', version: '' };
  } catch (error) {
    console.warn('获取操作系统信息失败:', error);
    return { sys: 'unknown', version: '' };
  }
}
